import React, { useState, useEffect, useRef } from 'react'
import GoldSiverHeader from '../HomePage/GoldSiverHeader'
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoint';
import { toast } from 'react-toastify';

function Pvp() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const battleId = searchParams.get('battle_id');
    const isPvPBattle = searchParams.get('pvp') === 'true';
    
    const [battleData, setBattleData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [battleStarted, setBattleStarted] = useState(false);
    const [playerPokemon, setPlayerPokemon] = useState([]);
    const [opponentPokemon, setOpponentPokemon] = useState([]);
    const [currentPlayerPoke, setCurrentPlayerPoke] = useState(null);
    const [currentOpponentPoke, setCurrentOpponentPoke] = useState(null);
    const [attacking, setAttacking] = useState(false);
    const [battleOver, setBattleOver] = useState(false);
    const [isWinner, setIsWinner] = useState(false);
    const [type, setType] = useState('Attack');
    const [isPlayerTurn, setIsPlayerTurn] = useState(false);
    const [attackList, setAttackList] = useState([]);
    const [currentAttack, setCurrentAttack] = useState({});
    
    const pollingIntervalRef = useRef(null);
    const componentMountedRef = useRef(true);
    const lastHpRef = useRef({ player: null, opponent: null });
    const animationTimeoutRef = useRef(null);
    
    // Load battle data
    const loadBattleData = async () => {
        if (!componentMountedRef.current) return;
        
        try {
            const response = await axios.get(`${API_ENDPOINTS.GET_PVP_BATTLE}?battle_id=${battleId}`);
            
            if (!response.data || !response.data.success) {
                toast.error('Failed to load battle');
                navigate('/map');
                return;
            }
            
            const battle = response.data.battle;
            
            // ONLY mark as over if battle_status is 'completed' AND winner_id is set
            if (battle.battle_status === 'completed' && battle.winner_id) {
                if (!battleOver) {
                    setBattleOver(true);
                    setIsWinner(battle.winner_id === (battle.is_player1 ? battle.player1_id : battle.player2_id));
                    setLoading(false);
                    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
                    // Call finishBattle to update statistics and distribute rewards
                    finishBattle();
                }
                return;
            }
            
            // Battle is active - ALWAYS reset battleOver to false if battle is active
            if (battle.battle_status === 'active') {
                setBattleOver(false);
                setIsWinner(false);
            }
            
            setBattleData(battle);
            
            // Update Pokemon lists
            const newPlayerPokemon = response.data.player_pokemon || [];
            const newOpponentPokemon = response.data.opponent_pokemon || [];
            
            // Get current Pokemon
            const playerPoke = newPlayerPokemon[response.data.current_player_index || 0] || null;
            const oppPoke = newOpponentPokemon[response.data.current_opponent_index || 0] || null;
            
            // Only show damage animation if HP actually changed and we haven't shown it recently
            if (oppPoke && !attacking) {
                const lastOppHp = lastHpRef.current.opponent;
                const currentOppHp = oppPoke.current_hp;
                
                // Only show animation if HP decreased and it's a new change
                if (lastOppHp !== null && lastOppHp > currentOppHp && lastOppHp !== currentOppHp) {
                    const damage = lastOppHp - currentOppHp;
                    setCurrentAttack({ damage });
                    if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
                    animationTimeoutRef.current = setTimeout(() => setCurrentAttack({}), 2000);
                }
                lastHpRef.current.opponent = currentOppHp;
            }
            
            if (playerPoke && !attacking) {
                const lastPlayerHp = lastHpRef.current.player;
                const currentPlayerHp = playerPoke.current_hp;
                
                // Only show animation if HP decreased and it's a new change
                if (lastPlayerHp !== null && lastPlayerHp > currentPlayerHp && lastPlayerHp !== currentPlayerHp) {
                    const damage = lastPlayerHp - currentPlayerHp;
                    setCurrentAttack({ damage: -damage });
                    if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
                    animationTimeoutRef.current = setTimeout(() => setCurrentAttack({}), 2000);
                }
                lastHpRef.current.player = currentPlayerHp;
            }
            
            setPlayerPokemon(newPlayerPokemon);
            setOpponentPokemon(newOpponentPokemon);
            setCurrentPlayerPoke(playerPoke);
            setCurrentOpponentPoke(oppPoke);
            
            if (playerPoke) {
                const attacks = [
                    playerPoke.attack_1,
                    playerPoke.attack_2,
                    playerPoke.attack_3,
                    playerPoke.attack_4
                ].filter(a => a && a.trim() !== '').map((name, i) => ({ name, slot: i + 1 }));
                setAttackList(attacks);
            }
            
            // Update turn status - CRITICAL for real-time updates
            if (battle.battle_status === 'active') {
                setBattleStarted(true);
                setIsPlayerTurn(response.data.is_player_turn || false);
            } else {
                setBattleStarted(false);
            }
            
            setLoading(false);
        } catch (error) {
            if (error.response?.status === 404) {
                toast.error('Battle not found');
                navigate('/map');
            }
        }
    };
    
    // Initial load
    useEffect(() => {
        componentMountedRef.current = true;
        
        // Reset HP refs on mount
        lastHpRef.current = { player: null, opponent: null };
        
        if (!battleId || !isPvPBattle) {
            toast.error('Invalid battle parameters');
            navigate('/map');
            return;
        }
        
        loadBattleData();
        
        // Poll every 1 second for real-time updates
        pollingIntervalRef.current = setInterval(() => {
            if (!battleOver && componentMountedRef.current && battleStarted) {
                loadBattleData();
            }
        }, 1000);
        
        return () => {
            componentMountedRef.current = false;
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
            if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
        };
    }, [battleId, isPvPBattle, navigate, battleOver, battleStarted]);
    
    const finishBattle = async () => {
        // Always call finishBattle API to update statistics and distribute rewards
        // Even if battleOver is true, we need to ensure statistics are updated
        try {
            const response = await axios.post(API_ENDPOINTS.FINISH_PVP_BATTLE, {
                battle_id: battleId,
                timeout: false
            });
            
            if (response.data?.success) {
                if (response.data.winner_reward) {
                    const reward = response.data.winner_reward;
                    toast.success(`${t('You received')}: ${reward.amount} ${t(reward.type)}!`);
                }
                
                if (!battleOver) {
                    setBattleOver(true);
                    setIsWinner(response.data.is_winner || false);
                    setTimeout(() => navigate('/map'), 3000);
                }
            } else {
                // If finishBattle fails, try to reload battle data
                if (!battleOver) {
                    await loadBattleData();
                }
            }
        } catch (error) {
            // Log error but don't prevent navigation if battle is already over
            if (!battleOver) {
                await loadBattleData();
            }
        }
    };
    
    const handleAttack = async (attackItem) => {
        if (!battleStarted || attacking || battleOver || !isPlayerTurn) {
            if (!isPlayerTurn && battleStarted) toast.warning(t('Not your turn!'));
            return;
        }
        
        if (!currentPlayerPoke || currentPlayerPoke.current_hp <= 0) {
            toast.warning(t('Cannot attack with a fainted Pokemon'));
            return;
        }
        
        try {
            setAttacking(true);
            const response = await axios.post(API_ENDPOINTS.ATTACK_PVP, {
                battle_id: battleId,
                attack_name: attackItem.name
            });
            
            if (response.data?.success) {
                const damage = response.data.damage || 0;
                const newHp = response.data.defender_new_hp || 0;
                
                // Update opponent HP immediately
                if (currentOpponentPoke) {
                    const oldHp = currentOpponentPoke.current_hp;
                    setCurrentOpponentPoke({
                        ...currentOpponentPoke,
                        current_hp: Math.max(0, newHp),
                        is_fainted: newHp <= 0
                    });
                    
                    setOpponentPokemon(prev => prev.map(p => 
                        p.id === currentOpponentPoke.id 
                            ? { ...p, current_hp: Math.max(0, newHp), is_fainted: newHp <= 0 }
                            : p
                    ));
                    
                    // Update last HP ref to prevent animation loop
                    lastHpRef.current.opponent = Math.max(0, newHp);
                }
                
                setCurrentAttack({ damage });
                
                // Wait for attack animation, then switch turn
                setTimeout(async () => {
                    setAttacking(false);
                    setCurrentAttack({});
                    
                    // Check if battle ended
                    if (response.data.battle_over && response.data.winner_id) {
                        // Battle ended - call finishBattle to update statistics
                        await finishBattle();
                    } else {
                        // Switch turn immediately - opponent can now attack
                        setIsPlayerTurn(false);
                        
                        // Reload battle data to sync state (will update turn via polling)
                        loadBattleData();
                    }
                }, 1000);
            } else {
                toast.error(response.data?.error || 'Attack failed');
                setAttacking(false);
            }
        } catch (error) {
            toast.error('Attack failed');
            setAttacking(false);
        }
    };
    
    const handleSwitchPokemon = async (poke) => {
        if (!battleStarted || !isPlayerTurn || attacking) {
            if (!isPlayerTurn && battleStarted) toast.warning(t('Not your turn!'));
            return;
        }
        
        if (poke.current_hp <= 0 || poke.is_fainted) {
            toast.warning(t('Cannot switch to a fainted Pokemon'));
            return;
        }
        
        try {
            const response = await axios.post(API_ENDPOINTS.SWITCH_PVP_POKEMON, {
                battle_id: battleId,
                slot_position: poke.slot_position
            });
            
            if (response.data?.success) {
                setCurrentPlayerPoke(poke);
                
                const attacks = [
                    poke.attack_1,
                    poke.attack_2,
                    poke.attack_3,
                    poke.attack_4
                ].filter(a => a && a.trim() !== '').map((name, i) => ({ name, slot: i + 1 }));
                setAttackList(attacks);
                
                setIsPlayerTurn(false);
                setType('Attack');
                
                toast.info(t('{{name}} entered the battle!', { name: poke.name }));
                
                await loadBattleData();
            } else {
                toast.error(response.data?.error || 'Switch failed');
            }
        } catch (error) {
            toast.error('Switch failed');
        }
    };
    
    // Auto-switch to next Pokemon if current one faints
    useEffect(() => {
        if (battleStarted && !battleOver && currentPlayerPoke && 
            currentPlayerPoke.current_hp <= 0 && !currentPlayerPoke.is_fainted && isPlayerTurn) {
            
            const nextPoke = playerPokemon.find(p => 
                p.id !== currentPlayerPoke.id && 
                p.current_hp > 0 && 
                !p.is_fainted
            );
            
            if (nextPoke) {
                setTimeout(() => {
                    handleSwitchPokemon(nextPoke);
                }, 1000);
            } else {
                // No more Pokemon - battle lost
                setBattleOver(true);
                setIsWinner(false);
            }
        }
    }, [currentPlayerPoke?.current_hp, currentPlayerPoke?.is_fainted, battleStarted, battleOver, playerPokemon, isPlayerTurn]);
    
    const renderPokemonBalls = (pokemonList) => {
        return (
            <div style={{ display: 'flex', gap: '4px', marginTop: '5px', justifyContent: 'center' }}>
                {pokemonList.map((poke, index) => (
                    <div 
                        key={index}
                        style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: poke.current_hp > 0 && !poke.is_fainted ? '#4CAF50' : '#666',
                            border: '1px solid #fff',
                            boxShadow: '0 0 3px rgba(0,0,0,0.5)'
                        }}
                        title={`${poke.name} - HP: ${poke.current_hp}/${poke.max_hp}`}
                    />
                ))}
            </div>
        );
    };
    
    if (loading) {
        return (
            <GoldSiverHeader previous={'/map'} title={t('PVP Battle')}>
                <div className="text-center p-5">
                    <p>{t('Loading battle')}...</p>
                </div>
            </GoldSiverHeader>
        );
    }
    
    return (
        <div>
            <GoldSiverHeader previous={'/map'} title={t('PVP Battle')}>
                <section className="ar_battle_area_section position-relative">
                    <div className="p-3 container-md">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="ar_batte_area">
                                    {/* Player Pokemon */}
                                    <div className={`your-pokemon ${attacking ? 'attacking' : ''} ${currentAttack?.damage < 0 ? 'hit' : ''}`}>
                                        {currentPlayerPoke && (
                                            <img 
                                                src={`/images/pokemon/back/${currentPlayerPoke.wild_id}.gif`}
                                                alt=""
                                                onError={(e) => { e.target.src = '/images/battle/default_pokemon_back.png'; }}
                                            />
                                        )}
                                        {currentAttack?.damage < 0 && (
                                            <h1 className='text-danger attack-damage'>{currentAttack.damage}</h1>
                                        )}
                                    </div>
                                    
                                    {/* Opponent Pokemon */}
                                    <div className={`opp-pokemon ${attacking ? 'attacking' : ''} ${currentAttack?.damage > 0 && !attacking ? 'hit' : ''}`}>
                                        {currentOpponentPoke && (
                                            <>
                                                <img 
                                                    src={`/images/pokemon/${currentOpponentPoke.wild_id}.gif`}
                                                    alt=""
                                                    onError={(e) => { e.target.src = '/images/battle/default_pokemon.png'; }}
                                                />
                                                {currentAttack?.damage > 0 && (
                                                    <h1 className='text-danger attack-damage'>-{currentAttack.damage}</h1>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    
                                    <div className="ar_battle_area_top">
                                        {/* Turn Indicator */}
                                        {battleStarted && !battleOver && (
                                            <div style={{
                                                position: 'absolute', top: '50px', left: '50%',
                                                transform: 'translateX(-50%)',
                                                color: isPlayerTurn ? '#4CAF50' : '#f44336',
                                                fontSize: '18px', fontWeight: 'bold',
                                                textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                                            }}>
                                                {isPlayerTurn ? t('Your Turn!') : t("Opponent's Turn")}
                                            </div>
                                        )}
                                        
                                        {/* Player HP */}
                                        <div className="ar_battleTop_left">
                                            <div className="ar_battle_left_hp_area">
                                                <div className="ar_battle_left_hp_text">
                                                    <p>{currentPlayerPoke?.name || t('Pokemon')}</p>
                                                    <p>Lv <span>{currentPlayerPoke?.level || 0}</span></p>
                                                </div>
                                                <img src="/images/battle/hp-bar-empty.png" alt="" />
                                                <div className='position-relative' style={{ 
                                                    height: '25px', 
                                                    width: `${Math.max(0, Math.min(100, (currentPlayerPoke?.current_hp || 0) * 100 / (currentPlayerPoke?.max_hp || 100)))}%`, 
                                                    top: "-35px", 
                                                    backgroundImage: 'url(/images/battle/hpRead.png)' 
                                                }} />
                                                {renderPokemonBalls(playerPokemon)}
                                            </div>
                                        </div>
                                        
                                        {/* Opponent HP */}
                                        <div className="ar_battleTop_right right">
                                            <div className="ar_battle_left_hp_area">
                                                <div className="ar_battle_left_hp_text">
                                                    <p>{currentOpponentPoke?.name || t('Opponent')}</p>
                                                    <p>Lv <span>{currentOpponentPoke?.level || 0}</span></p>
                                                </div>
                                                <img src="/images/battle/hp-bar-empty.png" alt="" />
                                                <div className='position-relative' style={{ 
                                                    height: '17px', 
                                                    width: `${Math.max(0, Math.min(100, (currentOpponentPoke?.current_hp || 0) * 100 / (currentOpponentPoke?.max_hp || 100)))}%`, 
                                                    top: "-27px", 
                                                    backgroundImage: 'url(/images/battle/hpRead.png)' 
                                                }} />
                                                {renderPokemonBalls(opponentPokemon)}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Battle Controls */}
                                    <div className="ar_battle_area_bottom">
                                        {battleStarted && !battleOver && battleData?.battle_status === 'active' && (
                                            <>
                                                <div className="ar_battle_bottom_btnAct">
                                                    <div className="ar_battle_btom_single" onClick={() => setType('Attack')}>
                                                        <a><img src={`/images/battle/${type === 'Attack' ? 'redAt.png' : 'selAt.png'}`} alt="" /></a>
                                                        <p>{t('Attack')}</p>
                                                    </div>
                                                    <div className="ar_battle_btom_single" onClick={() => setType('Pokemon')}>
                                                        <a><img src={`/images/battle/${type === 'Pokemon' ? 'redAt.png' : 'selAt.png'}`} alt="" /></a>
                                                        <p>{t('Pokemon')}</p>
                                                    </div>
                                                </div>
                                                
                                                {type === 'Attack' && (
                                                    <div className="ar_battle_bottom_bottm">
                                                        {attackList.length === 0 ? (
                                                            <div className="text-center text-white p-3">
                                                                <p>{t('No attacks available')}</p>
                                                            </div>
                                                        ) : (
                                                            attackList.map((item, index) => (
                                                                <div 
                                                                    key={index}
                                                                    className={`ar_battle_single_bottom ${!isPlayerTurn || attacking ? 'opacity-50' : ''}`}
                                                                    onClick={() => handleAttack(item)}
                                                                    style={{ cursor: isPlayerTurn && !attacking ? 'pointer' : 'not-allowed' }}
                                                                >
                                                                    <a><img src="/images/battle/Normal.png" alt="" /></a>
                                                                    <p>{item.name}</p>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                                
                                                {type === 'Pokemon' && (
                                                    <div className="ar_battle_bottom_bottm">
                                                        {playerPokemon
                                                            .filter(poke => 
                                                                poke.id !== currentPlayerPoke?.id && 
                                                                poke.current_hp > 0 && 
                                                                !poke.is_fainted
                                                            )
                                                            .map((poke, index) => (
                                                                <div 
                                                                    key={index} 
                                                                    className={`ar_battle_single_bottom ${!isPlayerTurn ? 'opacity-50' : ''}`}
                                                                    onClick={() => handleSwitchPokemon(poke)}
                                                                    style={{ cursor: isPlayerTurn ? 'pointer' : 'not-allowed' }}
                                                                >
                                                                    <a><img src="/images/battle/selAt.png" alt="" /></a>
                                                                    <div className='d-flex p-2 position-absolute w-100 h-100 top-0 align-items-center'>
                                                                        <img src={`/images/pokemon/icon/${poke.wild_id}.gif`} alt="" />
                                                                        <div className='p-2 d-flex flex-column' style={{ flexGrow: 1 }}>
                                                                            <h6>{poke.name}</h6>
                                                                            <div className="hp_red" style={{ height: "2px", width: "86%" }}>
                                                                                <div className="progress" style={{ width: `${poke.current_hp * 100 / poke.max_hp}%` }} />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        
                                        {battleOver && (
                                            <div className="text-center text-white p-5">
                                                <h3 style={{ color: isWinner ? '#4CAF50' : '#f44336', fontSize: '36px' }}>
                                                    {isWinner ? t('You Won!') : t('You Lost!')}
                                                </h3>
                                                <p style={{ marginTop: '20px' }}>{t('Redirecting to map')}...</p>
                                            </div>
                                        )}
                                        
                                        {battleData?.battle_status === 'pending' && !battleOver && (
                                            <div className="text-center text-white p-5">
                                                <p>{t('Waiting for opponent')}...</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </GoldSiverHeader>
        </div>
    );
}

export default Pvp;
